import { X } from 'lucide-react';
import React from 'react';
import { TooltipRenderProps } from 'react-joyride';

import { convertModuleNoun, useModuleNoun } from '@/lib/feature-flags';
import { Button } from '@/ui/button';

export const CustomTooltip = React.forwardRef<
  HTMLDivElement,
  TooltipRenderProps
>(
  (
    {
      index,
      step,
      closeProps,
      primaryProps,
      backProps,
      skipProps,
      tooltipProps,
      isLastStep,
      size,
    },
    ref
  ) => {
    const noun = useModuleNoun();
    return (
      <div
        {...tooltipProps}
        ref={ref}
        className="bg-white rounded-2xl w-[calc(100vw-32px)] sm:w-full max-w-[340px] max-h-[calc(100vh-32px)] overflow-y-auto p-3 sm:p-4 shadow-xl box-border"
      >
        <div className="flex justify-between items-center mb-1 sm:mb-2">
          <h4 className="capitalize font-bold text-lg sm:text-xl m-0 shrink-0 text-magical-moonlight-d3">
            {typeof step.title === 'string'
              ? convertModuleNoun(step.title, noun)
              : step.title}
          </h4>
          <Button
            {...closeProps}
            variant="ghost"
            size="icon"
            className="p-1 flex items-center justify-center shrink-0"
          >
            <X />
          </Button>
        </div>

        <div className="h-0.5 bg-slate-200 -mx-3 sm:-mx-4" />

        <div className="font-medium text-sm sm:text-base my-3 sm:my-4 whitespace-pre-wrap text-magical-moonlight-d4">
          {typeof step.content === 'string'
            ? convertModuleNoun(step.content, noun)
            : step.content}
        </div>

        <div className="flex justify-between items-center mt-2">
          <div className="flex items-center gap-1 sm:gap-2">
            <div className="font-medium text-xs sm:text-sm text-magical-moonlight-d2 w-12 sm:w-14">
              {index + 1} of {size}
            </div>
            <Button
              {...skipProps}
              variant="text"
              size="sm"
              className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3 text-magical-moonlight-d2 hover:text-magical-moonlight-d4"
            >
              Skip
            </Button>
          </div>
          <div className="flex gap-1 sm:gap-2">
            {index > 0 && (
              <Button
                {...backProps}
                variant="text"
                size="sm"
                className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
              >
                Previous
              </Button>
            )}
            <Button
              {...primaryProps}
              variant="secondary"
              size="sm"
              className="text-xs sm:text-sm h-8 sm:h-9 px-3 sm:px-4"
            >
              {isLastStep ? 'Finish' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    );
  }
);

CustomTooltip.displayName = 'CustomTooltip';
